import { normalizeSelectedActor } from 'app/shared/utils/actor-identifiers.util';
import { Injectable } from '@angular/core';
import { UserState } from './states/userState.state';
import { CompanyState } from './states/companyState.state';
import { Store } from '@ngrx/store';
import { map, Observable, of, Subscription, switchMap, take, tap } from 'rxjs';
import {
  selectConnectedUser,
  selectIsPartnerAdmin,
  selectUserCustomer,
  selectUserPartner,
  selectIsAcepPartner,
  selectIsMandataire,
  selectUserPreferences
} from './selectors/user.selectors';
import { selectCurrenteCompany } from './selectors/company.selectors';
import { setConnectedUser, setIsPartnerAdmin, setUserCustomer, setUserPartner , setIsAcepPartner, setIsMandataire, setUserPreference, setUserPreferences } from './actions/user.action';
import { setCurrentCompany } from './actions/company.actions';
import { PartnersService } from 'app/shared/services/partners.service';
import { UserService } from 'app/core/user/user.service';

@Injectable({
  providedIn: 'root'
})
export class StoreService {

  private connectedUser: any = null;
  private isPartnerAdmin: boolean = false;
  private isMandataire: boolean = false;
  private isAcepPartner: boolean = false;
  private userPartner: any = null;
  private userCustomer: any = null;
  private currentCompany: any = null;
  private userPreferences: { [code: string]: string } | null = null;

  private subscriptions: Subscription[] = [];

  constructor(private store: Store<UserState & CompanyState>,
    private readonly _userService: UserService,
    private _partnersService: PartnersService
  ) {
      this.store.select(selectConnectedUser).subscribe(user => this.connectedUser = user);
      this.store.select(selectIsPartnerAdmin).subscribe(isAdmin => this.isPartnerAdmin = isAdmin);
      this.store.select(selectIsMandataire).subscribe(isMandataire => this.isMandataire = isMandataire);
      this.store.select(selectIsAcepPartner).subscribe(isAcepPartner => this.isAcepPartner = isAcepPartner);
      this.store.select(selectUserPartner).subscribe(partner => this.userPartner = partner);
      this.store.select(selectUserCustomer).subscribe(customer => this.userCustomer = customer);
      this.store.select(selectCurrenteCompany).subscribe(company => this.currentCompany = company);
      this.store.select(selectUserPreferences).subscribe(preferences => this.userPreferences = preferences ?? null);
  }

  // Méthodes GET pour accéder aux valeurs stockées
  getConnectedUser(): any {
    return this.connectedUser;
  }

  IsPartnerAdmin(): boolean {
    return this.isPartnerAdmin;
  }

  IsMandataire(): boolean {
    return this.isMandataire;
  }

  IsAcepPartner(): boolean {
    return this.isAcepPartner;
  }

  getUserPartner(): any {
    return this.userPartner;
  }

  getUserCustomer(): any {
    return this.userCustomer;
  }

  getCurrentCompany(): any {
    return this.currentCompany;
  }

  getUserPreferences(): { [code: string]: string } | null {
    return this.userPreferences;
  }

  // Méthodes SET pour mettre à jour le store
  setConnectedUser(user: any): void {
    this.store.dispatch(setConnectedUser({ user }));
  }

  setIsPartnerAdmin(isAdmin: boolean): void {
    this.store.dispatch(setIsPartnerAdmin({ isPartnerAdmin: isAdmin }));
  }

  setIsMandataire(isMandataire: boolean): void {
    this.store.dispatch(setIsMandataire({ isMandataire: isMandataire }));
  }

  setIsAcepPartner(isAcepPartner: boolean): void {
    this.store.dispatch(setIsAcepPartner({ isAcepPartner: isAcepPartner }));
  }

  setUserPartner(partner: any): void {
    this.store.dispatch(setUserPartner({ userPartner: normalizeSelectedActor(partner) }));
  }

  setUserCustomer(customer: any): void {
    this.store.dispatch(setUserCustomer({ userCustomer: customer }));
  }

  setUserPreferences(userPreferences: { [code: string]: string }): void {
    this.store.dispatch(setUserPreferences({ userPreferences }));
  }

  setUserPreference(code: string, value: string): void {
    this.store.dispatch(setUserPreference({ code, value }));
  }

  setCurrentCompany(company: any): void {
    this.store.dispatch(setCurrentCompany({ company }));
  }

  initUserPartnerByCodeClient(): Observable<any> {
    const selectedPartner = this.getUserPartner();
  
    if (selectedPartner) {
      // Return the already-selected partner immediately
      return of(selectedPartner);
    }
  
    // No partner selected yet, retrieve sicaDbUser
    return this._userService.connectedUser$.pipe(
      take(1),
      switchMap(sicaDbUser => {
        if (sicaDbUser?.companyId) {
          return this._partnersService.getPartnerByCompanyId(sicaDbUser.companyId).pipe(
            map(partner => normalizeSelectedActor(partner)), 
            tap((partner: any) => {
              if (partner) {
                this.setUserPartner(partner);
                this._partnersService.setCurrentPartner(partner);
              }
            })
          );
        }
  
        // If no companyId, return null observable
        return of(null);
      })
    );
  }
  
}
